import { useState } from 'react'

import {
	CornerDownLeftIcon,
	Loader2Icon,
	RotateCcwIcon,
	SquareIcon,
	XIcon,
} from 'lucide-react'

import { cn } from '@/utils/ui/styles'

import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupTextarea,
} from '@/components/ui/input-group'

import type { ChatStatus } from 'ai'
import type {
	ComponentProps,
	FormEvent,
	FormEventHandler,
	HTMLAttributes,
	KeyboardEventHandler,
} from 'react'

export type PromptInputMessage = {
	text: string
}

export type PromptInputProps = Omit<
	HTMLAttributes<HTMLFormElement>,
	'onSubmit'
> & {
	onSubmit: (
		message: PromptInputMessage,
		event: FormEvent<HTMLFormElement>,
	) => void | Promise<void>
}

export const PromptInput = ({
	className,
	onSubmit,
	children,
	...props
}: PromptInputProps) => {
	const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault()

		const form = event.currentTarget
		const formData = new FormData(form)
		const text = ((formData.get('message') as string) ?? '').trim()

		if (!text) {
			return
		}

		form.reset()

		const result = onSubmit({ text }, event)
		if (result instanceof Promise) {
			result.catch(() => {
				// keep input cleared; caller surfaces errors via chat UI
			})
		}
	}

	return (
		<form
			className={cn('w-full', className)}
			onSubmit={handleSubmit}
			{...props}
		>
			<InputGroup className="overflow-hidden">{children}</InputGroup>
		</form>
	)
}

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>

export const PromptInputBody = ({
	className,
	...props
}: PromptInputBodyProps) => (
	<div className={cn('contents', className)} {...props} />
)

export type PromptInputTextareaProps = ComponentProps<typeof InputGroupTextarea>

export const PromptInputTextarea = ({
	onKeyDown,
	className,
	placeholder = 'Ask Noah…',
	...props
}: PromptInputTextareaProps) => {
	const [isComposing, setIsComposing] = useState(false)

	const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
		if (e.key === 'Enter') {
			if (isComposing || e.nativeEvent.isComposing) {
				onKeyDown?.(e)
				return
			}
			if (e.shiftKey) {
				onKeyDown?.(e)
				return
			}

			e.preventDefault()

			const form = e.currentTarget.form
			const submitButton = form?.querySelector(
				'button[type="submit"]',
			) as HTMLButtonElement | null
			if (submitButton?.disabled) {
				return
			}

			form?.requestSubmit()
			return
		}

		onKeyDown?.(e)
	}

	return (
		<InputGroupTextarea
			className={cn('field-sizing-content max-h-48 min-h-16', className)}
			name="message"
			onCompositionEnd={() => setIsComposing(false)}
			onCompositionStart={() => setIsComposing(true)}
			onKeyDown={handleKeyDown}
			placeholder={placeholder}
			{...props}
		/>
	)
}

export type PromptInputHeaderProps = Omit<
	ComponentProps<typeof InputGroupAddon>,
	'align'
>

/**
 * Block-start addon. Use to host the suggestion-bubble row above the textarea.
 */
export const PromptInputHeader = ({
	className,
	...props
}: PromptInputHeaderProps) => (
	<InputGroupAddon
		align="block-start"
		className={cn('order-first flex-wrap gap-1', className)}
		{...props}
	/>
)

export type PromptInputFooterProps = Omit<
	ComponentProps<typeof InputGroupAddon>,
	'align'
>

export const PromptInputFooter = ({
	className,
	...props
}: PromptInputFooterProps) => (
	<InputGroupAddon
		align="block-end"
		className={cn('justify-between gap-1', className)}
		{...props}
	/>
)

export type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>

export const PromptInputToolbar = ({
	className,
	...props
}: PromptInputToolbarProps) => (
	<div className={cn('flex items-center gap-1', className)} {...props} />
)

export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>

export const PromptInputButton = ({
	variant = 'ghost',
	type = 'button',
	className,
	size = 'icon-sm',
	...props
}: PromptInputButtonProps) => (
	<InputGroupButton
		className={cn(className)}
		size={size}
		type={type}
		variant={variant}
		{...props}
	/>
)

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
	status?: ChatStatus
	onStop?: () => void
	onRegenerate?: () => void
	showRegenerate?: boolean
}

export const PromptInputSubmit = ({
	className,
	// `outline` instead of vendor's `default` — the solid white "send" button
	// reads as out of place against the panel's dark muted palette. Same
	// button doubles as the stop affordance while streaming (icon swaps from
	// CornerDownLeft → Square), so this single default covers both states.
	variant = 'outline',
	size = 'icon-sm',
	status,
	onStop,
	onRegenerate,
	showRegenerate,
	children,
	type,
	onClick,
	...props
}: PromptInputSubmitProps) => {
	const isStreaming = status === 'streaming' || status === 'submitted'
	const isRegenerating = showRegenerate && status === 'submitted'

	const shouldBeAButton = isStreaming || isRegenerating

	let Icon: React.ReactNode

	if (!isRegenerating) {
		switch (status) {
			case 'submitted':
				Icon = <Loader2Icon className="size-4 animate-spin" />
				break
			case 'streaming':
				Icon = <SquareIcon className="size-4" />
				break
			case 'error':
				Icon = <XIcon className="size-4" />
				break
			default:
				Icon = <CornerDownLeftIcon className="size-4" />
				break
		}
	} else {
		Icon = <RotateCcwIcon className="size-4" />
	}

	return (
		<InputGroupButton
			aria-label={
				showRegenerate ? 'Regenerate' : isStreaming ? 'Stop' : 'Send message'
			}
			className={cn(className)}
			onClick={(e) => {
				if (isStreaming) {
					e.preventDefault()
					onStop?.()
				} else if (showRegenerate) {
					e.preventDefault()
					onRegenerate?.()
				}
				onClick?.(e)
			}}
			size={size}
			type={type ?? (shouldBeAButton ? 'button' : 'submit')}
			variant={variant}
			{...props}
		>
			{children ?? Icon}
		</InputGroupButton>
	)
}
